// @ts-strict-ignore
import { useState } from 'react'
import dayjs from 'dayjs'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DateErrorType, DateKeyType, DateType, SingleDateLabels } from '@node-space/ui-components'
import { formatDob } from '@node-space/utils'
import { DateFormat } from 'constants/General'

export const dateInitialValue: DateType = {
  day: '',
  month: '',
  year: '',
}

export const dateInitialErrors: DateErrorType = {
  day: false,
  month: false,
  year: false,
}

const dateErrorsAll: DateErrorType = {
  day: true,
  month: true,
  year: true,
}

export const useDateInput = () => {
  const { t } = useTranslation()

  const singleDateLabels: SingleDateLabels = {
    date: t('formFields.dateOfBirth'),
    day: t('formFields.day'),
    month: t('formFields.month'),
    year: t('formFields.year'),
    errorMessage: '',
  }

  const [dateErrors, setDateErrors] = useState<DateErrorType>(dateInitialErrors)

  const hasDateErrors: boolean = Object.values(dateErrors)?.indexOf(true) !== -1

  const getDateFields = (value: string): DateType => {
    const values = value?.split(',')
    const date = { ...dateInitialValue }
    Object.keys(date).forEach((key, index) => {
      date[key] = values?.[index] ?? ''
    })
    return date
  }

  const setDateInputValue = (date: DateType): string => {
    return Object.values(date).join()
  }

  const getDateErrors = (error: FieldError): DateErrorType => {
    return error?.type === 'required' ? dateErrorsAll : dateErrors
  }

  const getDateErrorText = (error: FieldError): string => {
    if (!!error?.message) {
      return error.message
    }

    const fieldLabels: DateType = {
      day: t('formFields.dayLowercase'),
      month: t('formFields.monthLowercase'),
      year: t('formFields.yearLowercase'),
    }

    const errorFields: string[] = Object.keys(dateErrors)
      ?.filter((key: DateKeyType) => dateErrors[key])
      ?.map((key: DateKeyType) => fieldLabels[key])

    if (!errorFields?.length) return ''

    let message = `${t('formFields.enterValidValue')} `
    if (errorFields?.length === 1) {
      message += `${errorFields.join()}.`
    } else {
      const lastIndex = errorFields?.length - 1
      message += `${errorFields?.slice(0, lastIndex).join(', ')} ${t('and')} ${
        errorFields[lastIndex]
      }.`
    }

    return message
  }

  const formatDateData = (value: string): string => {
    if (!value) return ''
    const dateValues = value.split('-')
    const parsed = dayjs(
      formatDob(dateValues[0], dateValues[1], dateValues[2]),
      DateFormat.YYYY_MM_DD
    )
    return parsed.isValid() ? parsed.format('DD,MM,YYYY') : ''
  }

  const formatDateInput = (value: string): string => {
    if (!value) return ''
    const dateValues = value?.split(',')
    const parsed = dayjs(
      formatDob(dateValues[0], dateValues[1], dateValues[2]),
      DateFormat.YYYY_MM_DD
    )
    return parsed.isValid() ? parsed.format(DateFormat.DD_MM_YYYY_) : ''
  }

  return {
    singleDateLabels,
    hasDateErrors,
    getDateFields,
    setDateInputValue,
    setDateErrors,
    getDateErrors,
    getDateErrorText,
    formatDateData,
    formatDateInput,
  }
}
